import type {OnboardingPosition} from '../onboardingSections'

const isPlainObject = (value: unknown): value is Record<string, any> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

/**
 * The preferences object from `GET user/preferences`, or null when what came back is not one.
 *
 * node-api returns the raw column text when it no longer parses — `preferences` is a TEXT
 * column and a value longer than it is cut short into invalid JSON. Spreading that string into
 * the next save would write `{"0": "{", "1": "\"", …}`, so it must never be used as preferences.
 */
export const readablePreferences = (value: unknown): Record<string, any> | null =>
  isPlainObject(value) ? value : null

/**
 * Whether these preferences show a returning user whose resume point is gone: the stored value
 * could not be read, or "Save and finish later" ran (`OnboardingWizard.savedAt`, stamped by
 * `onboarding/save-progress`) yet neither `progress` nor `resumeAt` survived. Such a user must
 * not be taken for a brand-new school: that seeds sample data into a tenant that already has
 * its own (the server refuses it) and drops them on step 1.
 */
export const lostResumePoint = (raw: unknown): boolean => {
  const prefs = readablePreferences(raw)
  if (!prefs) return raw !== null && raw !== undefined && raw !== ''
  const wizard = prefs.OnboardingWizard
  if (!isPlainObject(wizard)) return false
  if (wizard.progress?.stepSlug || wizard.resumeAt?.stepSlug) return false
  return Boolean(wizard.savedAt)
}

const asArray = (value: unknown): any[] => (Array.isArray(value) ? value : [])

/**
 * Where a returning user without a resume point picks up, from what the school already has on
 * the server (`GET onboarding/data`). Conservative: the screen whose data is saved, never one
 * past it, so the user sees their own work and continues with Next.
 *  - teachers saved -> the teacher review (its Next leads to Students);
 *  - classes with subjects -> Add Teachers (Academic Structure is complete);
 *  - classes only -> Academic Structure;
 *  - nothing -> null (start from the beginning).
 * Students are not in that response, so a user past the teachers comes back to their review.
 */
export const inferResumeFromOnboardingData = (apiResult: unknown): OnboardingPosition | null => {
  const source = apiResult as any
  const result = source?.result ?? source?.data ?? source ?? {}
  if (asArray(result?.teachers).length > 0) {
    return {stepSlug: 'assign-subjects', subStepSlug: 'review-teachers'}
  }
  const sections = asArray(result?.campuses).flatMap((campus) =>
    asArray(campus?.levels).flatMap((level) =>
      asArray(level?.grades).flatMap((grade) => asArray(grade?.sections))
    )
  )
  if (sections.length === 0) return null
  if (sections.some((section) => asArray(section?.subjects).length > 0)) {
    return {stepSlug: 'assign-subjects', subStepSlug: 'teachers'}
  }
  return {stepSlug: 'academic-structure', subStepSlug: null}
}
